/**
 * SYR-107 — the public lead generator, iframed on brandscience.ai/hypothesis.
 *
 * One page, four steps: form → code → progress → read-out, plus a terminal
 * notice for everything else. It also serves the emailed magic link at
 * /hypothesis/result?token=, which skips straight to the read-out.
 *
 * Deliberately outside every layout in App.tsx. UnprotectedLayout redirects an
 * authenticated user to /dashboard, which would break the emailed link for
 * anyone who happens to be logged in — and everyone at BrandScience is.
 *
 * Three things about living in a cross-origin iframe:
 *
 * 1. The API never returns 401 here, and this page never treats an error as a
 *    session problem. baseQueryWithAuth pops a blocking "Session Expired"
 *    modal and redirects to /sign-in on any 401, which would land on a visitor
 *    who has never had an account.
 * 2. The parent cannot measure us, so we post our height to it on every change.
 * 3. localStorage is partitioned in third-party frames (Safari ITP, Chrome
 *    storage partitioning), so it is best-effort only — the token lives in
 *    React state and the URL, which always work.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { useIntl } from 'react-intl';
import {
    useGetPublicHypothesisConfigQuery,
    useGetSignupConfigQuery,
    useGetPublicHypothesisResultQuery,
    useGetPublicHypothesisStatusQuery,
    useRecordPublicHypothesisCtaMutation,
    useRecordPublicHypothesisVideoMutation,
    useRequestPublicHypothesisMutation,
    useResendPublicHypothesisPinMutation,
    useUnsubscribePublicHypothesisQuery,
    useVerifyPublicHypothesisPinMutation,
} from '../../services/nestjs-base-api';
import { THEME } from '../../components/MarketerView/config/theme';
import { RequestForm } from './RequestForm';
import { PinStep } from './PinStep';
import { ProgressPanel } from './ProgressPanel';
import { ResultView } from './ResultView';
import { NoticeState, NoticeKind } from './NoticeState';
import { ElementVideoModal } from './ElementVideoModal';
import { BrandLogo } from '../../components/BrandLogo';

type Step = 'form' | 'pin' | 'progress' | 'result' | 'notice';

// Holds roughly the height of the form that follows it, so the frame does not
// shrink to a spinner and grow straight back.
const Loading: React.FC = () => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 252,
        }}
    >
        <Spin />
    </div>
);

const POLL_MS = 5000;
const STORAGE_KEY = 'bs_hypothesis_token';

interface Props {
    // The emailed magic link and the unsubscribe footer land on the same
    // component with a different job to do.
    mode?: 'tool' | 'result' | 'unsubscribe';
}

export const PublicHypothesisPage: React.FC<Props> = ({ mode = 'tool' }) => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const urlToken = searchParams.get('token') ?? '';

    const { data: config, isLoading: configLoading } = useGetPublicHypothesisConfigQuery();
    // Read at view time rather than at send time, which is the whole point:
    // a read-out is an emailed link opened days later, and the CTA has to be
    // right when it is READ, not when it was generated.
    const { data: signupConfig } = useGetSignupConfigQuery();

    const [token, setToken] = useState<string>(() => {
        if (urlToken) return urlToken;
        // Best-effort only: partitioned storage may throw or be empty.
        try {
            return window.localStorage.getItem(STORAGE_KEY) ?? '';
        } catch {
            return '';
        }
    });
    const [step, setStep] = useState<Step>(mode === 'tool' ? 'form' : 'result');
    const [notice, setNotice] = useState<NoticeKind>('review');
    const [email, setEmail] = useState('');
    const [brandName, setBrandName] = useState<string | null>(null);
    const [targetDomain, setTargetDomain] = useState<string | null>(null);
    const [pinInvalid, setPinInvalid] = useState(false);
    const [resent, setResent] = useState(false);
    const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
    const [startedAt] = useState(() => Date.now());
    const [handedOverToEmail, setHandedOverToEmail] = useState(false);
    const [video, setVideo] = useState<{ id: string; title: string } | null>(null);

    /**
     * Attribution handed across the iframe boundary by brandscience.ai.
     *
     * It cannot be read here directly: this page is a different origin, so its
     * own document.referrer says "brandscience.ai" and its own PostHog id is a
     * second anonymous person. Everything below arrives on the query string
     * because the hosting page put it there.
     *
     * Read once at mount — the query string never changes for a given visit,
     * and re-reading it would just be a way to get it wrong later.
     */
    const [attribution] = useState(() => {
        const q = new URLSearchParams(window.location.search);
        const take = (key: string) => q.get(key) || undefined;
        return {
            referrer: take('ref'),
            landingPage: take('lp'),
            utmSource: take('utm_source'),
            utmMedium: take('utm_medium'),
            utmCampaign: take('utm_campaign'),
            utmTerm: take('utm_term'),
            utmContent: take('utm_content'),
            posthogDistinctId: take('phid'),
        };
    });

    // Framed by brandscience.ai. Computed once: it decides whether this page
    // repeats the hosting page's headline, and it is the same signal the
    // height handshake keys off.
    const [embedded] = useState(() => {
        try {
            return window.parent !== window;
        } catch {
            // Cross-origin access can throw in some browsers; if we cannot
            // tell, assume framed — a missing headline is a smaller fault than
            // a duplicated one.
            return true;
        }
    });

    const [request, requestState] = useRequestPublicHypothesisMutation();
    const [resend] = useResendPublicHypothesisPinMutation();
    const [verify, verifyState] = useVerifyPublicHypothesisPinMutation();
    const [recordCta] = useRecordPublicHypothesisCtaMutation();
    const [recordVideo] = useRecordPublicHypothesisVideoMutation();

    // ------------------------------------------------------------- polling

    const polling = step === 'progress' && !!token && !handedOverToEmail;
    const { data: status } = useGetPublicHypothesisStatusQuery(
        { token },
        { skip: !polling, pollingInterval: POLL_MS },
    );

    const wantResult = step === 'result' && !!token;
    const {
        data: result,
        isFetching: resultFetching,
        isError: resultErrored,
    } = useGetPublicHypothesisResultQuery({ token }, { skip: !wantResult });

    const { data: unsubscribed } = useUnsubscribePublicHypothesisQuery(
        { token: urlToken },
        { skip: mode !== 'unsubscribe' || !urlToken },
    );

    // ------------------------------------------------- document title + robots

    /**
     * Name the brand in the tab.
     *
     * This page is opened days later from an email, usually alongside a dozen
     * other tabs, so "BrandScience.ai" is useless for finding it again. Brand
     * first because tabs truncate from the right: "FINERRINGS: The Hyp..." is
     * still identifiable, "The Hypothesis for FIN..." is not.
     *
     * Restored on unmount — this is a single-page app, and leaving a brand
     * name in the tab after navigating elsewhere is worse than never setting it.
     */
    useEffect(() => {
        const previous = document.title;
        const brand = result?.brandName || brandName;
        document.title = brand
            ? `${brand}: The Hypothesis | BrandScience.ai`
            : 'The Hypothesis | BrandScience.ai';
        return () => {
            document.title = previous;
        };
    }, [result?.brandName, brandName]);

    /**
     * Keep the read-out out of search results.
     *
     * The URL carries a token, so an indexed copy would put one brand's
     * private read-out in front of anyone who searched for them. The marketing
     * page at brandscience.ai/hypothesis is the one meant to be found; this is
     * not.
     */
    useEffect(() => {
        const tag = document.createElement('meta');
        tag.name = 'robots';
        tag.content = 'noindex, nofollow';
        document.head.appendChild(tag);
        return () => {
            document.head.removeChild(tag);
        };
    }, []);

    // -------------------------------------------------- iframe height contract

    const rootRef = useRef<HTMLDivElement | null>(null);
    // Tell the parent which stage is showing. The read-out is built wider than
    // the form (1040 vs 720), and the host page cannot see inside a
    // cross-origin frame to know the difference — so it is told, the same way
    // it is told the height. Sent on every step change; the parent widens on
    // 'result' and narrows back on anything else.
    useEffect(() => {
        if (!embedded || window.parent === window) return;
        window.parent.postMessage(
            { type: 'bs-hypothesis-stage', stage: step },
            '*',
        );
    }, [embedded, step]);

    useEffect(() => {
        const node = rootRef.current;
        if (!node || !embedded) return;

        const post = () => {
            // The parent checks event.origin; we cannot know its origin ahead
            // of time (brandscience.ai vs www), so '*' here is correct and
            // safe — the payload is a number, not a secret.
            // getBoundingClientRect over scrollHeight: it is fractional, so a
            // sub-pixel row does not get rounded away into a scrollbar. The
            // small addition absorbs margin collapse at the bottom edge.
            window.parent.postMessage(
                {
                    type: 'bs-hypothesis-height',
                    height: Math.ceil(node.getBoundingClientRect().height) + 8,
                },
                '*',
            );
        };
        post();
        const observer = new ResizeObserver(post);
        observer.observe(node);
        return () => observer.disconnect();
    }, [embedded, step, result, status]);

    // --------------------------------------------------- state transitions

    useEffect(() => {
        if (!status) return;
        if (status.brandName) setBrandName(status.brandName);
        if (status.state === 'ready') {
            setStep('result');
        } else if (
            status.state === 'review' ||
            status.state === 'insufficient_data' ||
            status.state === 'failed' ||
            status.state === 'delayed' ||
            status.state === 'link_expired' ||
            status.state === 'unavailable'
        ) {
            setNotice(status.state as NoticeKind);
            setStep('notice');
        }
    }, [status]);

    useEffect(() => {
        if (!result) return;
        if (result.state !== 'ready') {
            if (result.state === 'running') {
                setStep('progress');
                return;
            }
            setNotice(result.state as NoticeKind);
            setStep('notice');
        }
    }, [result]);

    // Hand over to the email once the wait has run long. The email is the
    // contract; this page is the bonus, and a bar that never finishes is worse
    // than an honest "we'll mail you".
    useEffect(() => {
        if (step !== 'progress' || !config?.waitMinutes) return;
        const timer = window.setTimeout(
            () => setHandedOverToEmail(true),
            config.waitMinutes * 60_000 - (Date.now() - startedAt),
        );
        return () => window.clearTimeout(timer);
    }, [step, config?.waitMinutes, startedAt]);

    const persistToken = useCallback((value: string) => {
        setToken(value);
        try {
            window.localStorage.setItem(STORAGE_KEY, value);
        } catch {
            // Partitioned or blocked storage. The token is in React state,
            // which is all this session needs.
        }
    }, []);

    /**
     * Play an element explainer.
     *
     * EMBEDDED: ask the hosting page. A modal rendered in here is positioned
     * against the IFRAME's viewport, which is sized to the content and may be
     * scrolled halfway out of the reader's view — the video would open small,
     * somewhere they are not looking, and clip. The parent has the whole
     * window and opens it properly.
     *
     * STANDALONE (the emailed link): open it here, because there is no parent
     * to ask.
     */
    const playVideo = useCallback(
        (videoId: string, title: string, element: string) => {
            if (embedded && window.parent !== window) {
                // The element rides along so the host page's video_opened
                // event can say which Booster the read-out click was for.
                window.parent.postMessage(
                    { type: 'bs-hypothesis-video', videoId, title, element },
                    '*',
                );
            } else {
                setVideo({ id: videoId, title });
            }
            // Which explainers get watched is the clearest signal we have about
            // which elements people do not already understand.
            if (token) {
                recordVideo({ token, videoId, element });
            }
        },
        [embedded, token, recordVideo],
    );

    // ------------------------------------------------------------- actions

    const onSubmit = async (values: {
        url: string;
        email: string;
        companyWebsite: string;
    }) => {
        setFieldError(null);
        try {
            const response = await request({ ...values, attribution }).unwrap();
            setEmail(values.email);
            setBrandName(response.brandName ?? null);
            setTargetDomain(response.targetDomain ?? null);

            if (response.state === 'pin_sent') {
                setStep('pin');
            } else {
                setNotice(response.state as NoticeKind);
                setStep('notice');
            }
        } catch (error) {
            // A 400 here is a field problem, never an auth problem. Read the
            // shape the API sends and put it on the offending input.
            const data = (error as { data?: { field?: string; message?: string } })?.data;
            setFieldError({
                field: data?.field ?? 'url',
                message:
                    data?.message ??
                    intl.formatMessage({
                        id: 'publicHypothesis.form.genericError',
                        defaultMessage: 'That did not work. Check both fields and try again.',
                    }),
            });
        }
    };

    const onVerify = async (pin: string) => {
        setPinInvalid(false);
        const response = await verify({ email, pin }).unwrap();
        if (response.state === 'pin_invalid') {
            setPinInvalid(true);
            return;
        }
        if (response.token) persistToken(response.token);
        if (response.state === 'ready') {
            setStep('result');
        } else if (response.state === 'running') {
            setStep('progress');
        } else {
            setNotice(response.state as NoticeKind);
            setStep('notice');
        }
    };

    const onResend = async () => {
        await resend({ email });
        setResent(true);
        window.setTimeout(() => setResent(false), 8000);
    };

    // -------------------------------------------------------------- render

    /**
     * The one call to action, in its two states.
     *
     * Closed is /sign-up on this same origin: the stable front door, which
     * already redirects to the Charter application if the intake happens to be
     * open, so an old link is never wrong.
     *
     * Open sends them to the website's /charter, which is the page that sells
     * the cohort — the application form is a step past where someone finishing
     * a free read-out is. It is an absolute URL to another host, and the CTA
     * already carries target="_top", so it escapes the iframe on
     * brandscience.ai/hypothesis rather than loading a page inside a frame.
     *
     * Nothing is hardcoded about the intake being open: the flag is read on
     * every view, so the day it closes this reverts on its own.
     */
    const signUpUrl = useMemo(() => `${window.location.origin}/sign-up`, []);
    const charterOpen = signupConfig?.charterCohortEnabled === true;

    const body = () => {
        if (configLoading) {
            // Sized close to the form it is about to become. Left to itself a
            // spinner is ~130px, so the frame would shrink to fit it and then
            // grow again a moment later — two movements and a scrollbar in
            // between, for a state nobody is meant to notice.
            return <Loading />;
        }

        // The kill switch stops the RUN, not the form. Paused, the form below
        // still collects and looks exactly as it does live; a submission lands
        // in the review queue for a manual run and the visitor reads the same
        // "thanks, we have this" as a gated one. Nothing on the page says the
        // tool is off. The only thing withheld is the PIN promise.
        const paused = !!config && !config.enabled;

        if (mode === 'unsubscribe') {
            return unsubscribed?.ok ? (
                <NoticeState kind="unsubscribed" />
            ) : (
                <NoticeState kind="link_expired" />
            );
        }

        if (step === 'notice') {
            // Server-held copy wins for the unavailable notice, so the wording
            // is Steve's to change without a deploy.
            const overrideBody =
                notice === 'unavailable' ? config?.disabledMarkdown || undefined : undefined;
            return <NoticeState kind={notice} overrideBody={overrideBody} />;
        }

        if (step === 'result') {
            // An error must never look like loading. Before this, a failed
            // request (a 429, a dropped connection) left `result` undefined
            // with nothing in flight, and the spinner ran forever — the reader
            // sits watching a page that will never finish and has no idea why.
            if (resultErrored) {
                return <NoticeState kind="load_failed" />;
            }
            if (resultFetching || !result) {
                return <Loading />;
            }
            if (result.state !== 'ready') return <NoticeState kind={notice} />;
            return (
                <ResultView
                    result={result}
                    signUpUrl={signUpUrl}
                    charterOpen={charterOpen}
                    publicSignupEnabled={signupConfig?.publicSignupEnabled === true}
                    freeTierSignupEnabled={signupConfig?.freeTierSignupEnabled !== false}
                    // Deliberately not awaited and never blocking: the reader
                    // is already navigating away, and a failed beacon must not
                    // cost them the click.
                    onCtaClick={() => {
                        if (token) recordCta({ token });
                    }}
                    onPlayVideo={playVideo}
                />
            );
        }

        if (step === 'progress') {
            return (
                <ProgressPanel
                    brandName={brandName}
                    stage={status?.stage}
                    queuePosition={status?.queuePosition}
                    elapsedSeconds={status?.elapsedSeconds}
                    handedOverToEmail={handedOverToEmail}
                />
            );
        }

        if (step === 'pin') {
            return (
                <PinStep
                    email={email}
                    brandName={brandName}
                    targetDomain={targetDomain}
                    submitting={verifyState.isLoading}
                    invalid={pinInvalid}
                    onVerify={onVerify}
                    onResend={onResend}
                    resent={resent}
                />
            );
        }

        return (
            <RequestForm
                consentMarkdown={config?.consentMarkdown ?? ''}
                submitting={requestState.isLoading}
                onSubmit={onSubmit}
                fieldError={fieldError}
                embedded={embedded}
                paused={paused}
            />
        );
    };

    return (
        <ConfigProvider theme={{ token: { colorPrimary: THEME.primary } }}>
            <div
                ref={rootRef}
                style={{
                    // Transparent so the framing page's own background shows
                    // through and the embed does not read as a pasted-in box.
                    background: 'transparent',
                    // Deliberately NOT THEME.fontFamily. That token is the
                    // system stack, pinned to match the v3 orbital; this page
                    // is a marketing surface and should carry the brand face
                    // (Plus Jakarta Sans) it inherits from the ConfigProvider.

                    // The host page supplies its own card and padding, so only
                    // add our own when standing alone.
                    padding: embedded ? '20px' : '24px 20px',
                    // The read-out gets the width, the form does not. 720 for
                    // everything made the result a skinny column in a desktop
                    // viewport (Adir: "why is it so thin?") — but a form
                    // completes better narrow, so only the result widens. The
                    // extra width is used by the scorecard's two-up grid, not
                    // by the prose, which keeps its ch-based measure.
                    maxWidth: step === 'result' ? 1040 : 720,
                    margin: '0 auto',
                }}
            >
                {/* Standing alone only. The magic link opens a bare page on
                    app.brandscience.ai with nothing on it that says who sent
                    it, and this arrives by email days after they asked — so
                    the mark is what makes it recognisable rather than
                    suspicious. Inside the iframe the hosting page's own header
                    already carries it, and a second one reads as a fault. */}
                {embedded ? null : (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                        <BrandLogo size={36} fontSize={20} color={THEME.textBase} />
                    </div>
                )}
                {body()}
                <ElementVideoModal
                    videoId={video?.id ?? null}
                    title={video?.title ?? null}
                    onClose={() => setVideo(null)}
                />
            </div>
        </ConfigProvider>
    );
};
